'use client';

import type { MrMapPreview } from './runtime/net/mysteryRoomApi';
import type { MysteryRoomMapDefinition } from './runtime/world/mapTypes';

type MapCardTone = 'cyan' | 'amber' | 'slate';

type Props = {
  title: string;
  subtitle?: string;
  description?: string;
  selected?: boolean;
  disabled?: boolean;
  badge?: string;
  tone?: MapCardTone;
  preview?: MrMapPreview | null;
  mapDefinition?: MysteryRoomMapDefinition;
  onClick?: () => void;
};

const TONE_CLASS: Record<MapCardTone, string> = {
  cyan: 'border-site-accent/50 bg-site-accent/10',
  amber: 'border-site-warning/45 bg-site-warning/10',
  slate: 'border-site-border bg-site-surface/[0.035]',
};

export default function MysteryRoomMapCard({
  title,
  subtitle,
  description,
  selected,
  disabled,
  badge,
  tone = 'slate',
  preview,
  mapDefinition,
  onClick,
}: Props) {
  const resolvedPreview = preview ?? mapPreviewFromDefinition(mapDefinition);
  const content = (
    <>
      <MapMiniPreview preview={resolvedPreview} selected={selected} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-site-text">{title}</div>
            {subtitle && <div className="mt-1 truncate text-xs text-site-muted">{subtitle}</div>}
          </div>
          {badge && (
            <span className="shrink-0 rounded-full border border-site-border bg-site-surface/[0.06] px-2 py-1 text-[0.6rem] uppercase tracking-[0.14em] text-site-secondary">
              {badge}
            </span>
          )}
        </div>
        {description && <div className="mt-2 line-clamp-2 text-xs leading-5 text-site-muted">{description}</div>}
        {resolvedPreview && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <MapMetaChip value={`${Math.round(resolvedPreview.width)}x${Math.round(resolvedPreview.height)}`} />
            <MapMetaChip value={`${resolvedPreview.objectsCount} obj`} />
            <MapMetaChip value={`${resolvedPreview.lightsCount} light`} />
          </div>
        )}
      </div>
    </>
  );

  const className = `group relative flex w-full gap-3 overflow-hidden rounded-[16px] border p-3 text-left transition ${
    selected ? `${TONE_CLASS.cyan} shadow-[0_0_32px_rgba(34,211,238,0.10)]` : TONE_CLASS[tone]
  } ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-site-border hover:bg-site-surface/[0.06]'}`;

  if (!onClick) {
    return <div className={className}>{content}</div>;
  }

  return (
    <button type="button" disabled={disabled} onClick={onClick} className={className}>
      {content}
    </button>
  );
}

function MapMiniPreview({ preview, selected }: { preview?: MrMapPreview | null; selected?: boolean }) {
  const safePreview = preview && preview.width > 0 && preview.height > 0 ? preview : null;
  const rooms = safePreview?.rooms.slice(0, 10) ?? [];
  const spawns = safePreview?.spawnSlots.slice(0, 4) ?? [];

  return (
    <div className={`relative h-20 w-28 shrink-0 overflow-hidden rounded-[12px] border bg-site-bg shadow-inner ${
      selected ? 'border-site-accent/45' : 'border-site-border'
    }`}>
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:14px_14px]" />
      {rooms.length > 0 ? (
        rooms.map((room) => (
          <span
            key={room.id}
            className="absolute rounded-[4px] border border-site-border"
            style={{
              left: `${percent(room.x, safePreview!.width)}%`,
              top: `${percent(room.y, safePreview!.height)}%`,
              width: `${Math.max(6, percent(room.width, safePreview!.width))}%`,
              height: `${Math.max(8, percent(room.height, safePreview!.height))}%`,
              backgroundColor: room.color,
              opacity: 0.72,
            }}
          />
        ))
      ) : (
        <span className="absolute inset-4 rounded-[10px] border border-site-accent/24 bg-site-accent/10" />
      )}
      {spawns.map((spawn, index) => (
        <span
          key={`${spawn.x}:${spawn.y}:${index}`}
          className="absolute size-2 rounded-full border border-site-border bg-site-success shadow-[0_0_10px_rgba(52,211,153,0.65)]"
          style={{
            left: `calc(${percent(spawn.x, safePreview!.width)}% - 4px)`,
            top: `calc(${percent(spawn.y, safePreview!.height)}% - 4px)`,
          }}
        />
      ))}
      <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-site-bg/92 to-transparent" />
      {selected && (
        <div className="absolute bottom-2 left-2 rounded-full border border-site-accent/40 bg-site-accent/20 px-2 py-0.5 text-[0.55rem] font-black uppercase tracking-[0.14em] text-site-accent">
          selected
        </div>
      )}
    </div>
  );
}

function MapMetaChip({ value }: { value: string }) {
  return (
    <span className="rounded-full border border-site-border bg-site-bg/55 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-site-muted">
      {value}
    </span>
  );
}

function mapPreviewFromDefinition(map?: MysteryRoomMapDefinition): MrMapPreview | null {
  if (!map) return null;
  return {
    width: map.width,
    height: map.height,
    rooms: map.rooms.slice(0, 10).map((room) => {
      const rect = room.floor[0] ?? { x: 0, y: 0, width: 0, height: 0 };
      return {
        id: room.id,
        title: room.title,
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        color: colorNumberToCss(room.color),
      };
    }),
    spawnSlots: map.spawnSlots.map((slot) => ({ x: slot.x, y: slot.y })),
    objectsCount: map.customObjects.length + map.props.length + map.interactables.length,
    lightsCount: map.lights.length,
  };
}

function percent(value: number, total: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.min(100, (value / total) * 100));
}

function colorNumberToCss(value: number): string {
  const hex = Math.max(0, Math.min(0xffffff, value || 0)).toString(16).padStart(6, '0');
  return `#${hex}`;
}
